import React, { useState } from 'react';
import './ApiDocs.css';

const styles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
};

const ApiDocs = () => {
  const [selectedCategory, setSelectedCategory] = useState('autenticacao');
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);

  const categories = [
    {
      id: 'autenticacao',
      name: 'Autenticação',
      icon: '🔐',
      endpoints: [
        {
          method: 'POST',
          path: '/api/auth/login',
          description: 'Realizar login',
          public: true,
          body: {
            username: 'string',
            password: 'string'
          },
          response: {
            token: 'string',
            user: { id: 'number', username: 'string', nome: 'string', role: 'string' }
          }
        },
        {
          method: 'GET',
          path: '/api/auth/check',
          description: 'Verificar token',
          public: false,
          response: {
            authenticated: true,
            user: { id: 'number', username: 'string', nome: 'string', role: 'string' }
          }
        },
        {
          method: 'POST',
          path: '/api/auth/logout',
          description: 'Realizar logout',
          public: false
        },
        {
          method: 'PUT',
          path: '/api/auth/profile',
          description: 'Atualizar perfil',
          public: false,
          body: {
            nome: 'string',
            email: 'string'
          }
        },
        {
          method: 'PUT',
          path: '/api/auth/change-password',
          description: 'Alterar senha',
          public: false,
          body: {
            currentPassword: 'string',
            newPassword: 'string (mínimo 12 caracteres)'
          }
        },
        {
          method: 'GET',
          path: '/api/auth/users',
          description: 'Listar usuários (admin)',
          public: false,
          admin: true
        },
        {
          method: 'POST',
          path: '/api/auth/users',
          description: 'Criar usuário (admin)',
          public: false,
          admin: true,
          body: {
            username: 'string',
            password: 'string',
            nome: 'string',
            email: 'string',
            role: 'string'
          }
        },
        {
          method: 'DELETE',
          path: '/api/auth/users/:id',
          description: 'Deletar usuário (admin)',
          public: false,
          admin: true
        }
      ]
    },
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: '📊',
      endpoints: [
        {
          method: 'GET',
          path: '/api/dashboard/stats',
          description: 'Estatísticas gerais',
          public: false
        },
        {
          method: 'GET',
          path: '/api/dashboard/analytics',
          description: 'Análises detalhadas',
          public: false,
          queryParams: {
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/dashboard/recent-solicitacoes',
          description: '10 solicitações recentes',
          public: false
        },
        {
          method: 'GET',
          path: '/api/dashboard/solicitacoes-chart',
          description: 'Gráfico últimos 7 dias',
          public: false
        }
      ]
    },
    {
      id: 'pacientes',
      name: 'Pacientes',
      icon: '👥',
      endpoints: [
        {
          method: 'GET',
          path: '/api/pacientes',
          description: 'Listar pacientes',
          public: false,
          queryParams: {
            page: 'number (default: 1)',
            limit: 'number (default: 20)',
            search: 'string (opcional)',
            sexo: 'M/F (opcional)',
            cidade: 'string (opcional)',
            idadeMin: 'number (opcional)',
            idadeMax: 'number (opcional)',
            sortBy: 'string (default: ID)',
            sortOrder: 'ASC/DESC (default: DESC)'
          }
        },
        {
          method: 'GET',
          path: '/api/pacientes/:id',
          description: 'Detalhes do paciente',
          public: false
        },
        {
          method: 'GET',
          path: '/api/pacientes/:id/solicitacoes',
          description: 'Solicitações do paciente',
          public: false
        }
      ]
    },
    {
      id: 'exames',
      name: 'Exames',
      icon: '🔬',
      endpoints: [
        {
          method: 'GET',
          path: '/api/exames',
          description: 'Listar exames',
          public: false,
          queryParams: {
            limit: 'number (default: 100)',
            search: 'string (opcional)',
            tipo: 'string (opcional)',
            unidade: 'string (opcional)',
            sortBy: 'string (default: DESCRICAO)',
            sortOrder: 'ASC/DESC (default: ASC)'
          }
        },
        {
          method: 'GET',
          path: '/api/exames/:id',
          description: 'Detalhes do exame',
          public: false
        },
        {
          method: 'GET',
          path: '/api/lab-apoio',
          description: 'Laboratórios de Apoio',
          public: false,
          queryParams: {
            search: 'string (opcional)',
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)',
            situacao: 'string (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/solicitacao-exames/:id/pdf',
          description: 'PDF do resultado',
          public: false,
          responseType: 'application/pdf'
        }
      ]
    },
    {
      id: 'solicitacoes',
      name: 'Solicitações',
      icon: '📝',
      endpoints: [
        {
          method: 'GET',
          path: '/api/solicitacoes',
          description: 'Listar solicitações',
          public: false,
          queryParams: {
            page: 'number (default: 1)',
            limit: 'number (default: 20)',
            search: 'string (opcional)',
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)',
            usuario: 'string (opcional)',
            situacao: 'string (opcional)',
            convenio: 'string (opcional)',
            sortBy: 'string (default: DATA)',
            sortOrder: 'ASC/DESC (default: DESC)'
          }
        },
        {
          method: 'GET',
          path: '/api/solicitacoes/protocolo/:protocolo',
          description: 'Detalhes por protocolo',
          public: false
        },
        {
          method: 'POST',
          path: '/api/solicitacoes/investigar',
          description: 'Investigar solicitação (debug)',
          public: false,
          body: {
            solicitacaoId: 'number'
          }
        },
        {
          method: 'GET',
          path: '/api/solicitacoes/:solicitacaoId/anexos',
          description: 'Listar anexos',
          public: false
        }
      ]
    },
    {
      id: 'atendimentos',
      name: 'Atendimentos',
      icon: '🏥',
      endpoints: [
        {
          method: 'GET',
          path: '/api/atendimentos',
          description: 'Listar atendimentos',
          public: false,
          queryParams: {
            page: 'number (default: 1)',
            limit: 'number (default: 20)',
            search: 'string (opcional)',
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)',
            situacao: 'string (opcional)',
            sortBy: 'string (default: DATA)',
            sortOrder: 'ASC/DESC (default: DESC)'
          }
        },
        {
          method: 'GET',
          path: '/api/atendimentos/:id',
          description: 'Detalhes do atendimento',
          public: false
        }
      ]
    },
    {
      id: 'coletas',
      name: 'Coletas Domiciliares',
      icon: '🚑',
      endpoints: [
        {
          method: 'GET',
          path: '/api/coletas-domiciliares',
          description: 'Listar coletas',
          public: false,
          queryParams: {
            page: 'number (default: 1)',
            limit: 'number (default: 20)',
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)',
            situacao: 'string (opcional)',
            pacienteNome: 'string (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/coletas-domiciliares/:id',
          description: 'Detalhes da coleta',
          public: false
        }
      ]
    },
    {
      id: 'orcamentos',
      name: 'Orçamentos',
      icon: '💰',
      endpoints: [
        {
          method: 'GET',
          path: '/api/orcamentos',
          description: 'Listar orçamentos',
          public: false,
          queryParams: {
            page: 'number (default: 1)',
            limit: 'number (default: 20)',
            search: 'string (opcional)',
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)',
            situacao: 'string (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/orcamentos/:id',
          description: 'Detalhes do orçamento',
          public: false
        }
      ]
    },
    {
      id: 'resultados',
      name: 'Resultados',
      icon: '📋',
      endpoints: [
        {
          method: 'GET',
          path: '/api/resultados',
          description: 'Listar resultados',
          public: false,
          queryParams: {
            search: 'string (opcional)',
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)',
            situacao: 'string (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/resultados/:atendimentoId',
          description: 'Resultados do atendimento',
          public: false
        }
      ]
    },
    {
      id: 'financeiro',
      name: 'Financeiro',
      icon: '💵',
      endpoints: [
        {
          method: 'GET',
          path: '/api/financeiro/resumo',
          description: 'Resumo financeiro',
          public: false,
          queryParams: {
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/financeiro/titulos-receber',
          description: 'Títulos a receber',
          public: false,
          queryParams: {
            page: 'number (default: 1)',
            limit: 'number (default: 20)',
            situacao: 'string (opcional)',
            vencimento: 'YYYY-MM-DD (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/financeiro/caixas',
          description: 'Movimentações de caixa',
          public: false,
          queryParams: {
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/financeiro/movimento-caixa',
          description: 'Movimento detalhado',
          public: false,
          queryParams: {
            data: 'YYYY-MM-DD (opcional, default: hoje)'
          }
        }
      ]
    },
    {
      id: 'medicos',
      name: 'Médicos',
      icon: '👨‍⚕️',
      endpoints: [
        {
          method: 'GET',
          path: '/api/medicos',
          description: 'Listar médicos',
          public: false,
          queryParams: {
            page: 'number (default: 1)',
            limit: 'number (default: 20)',
            search: 'string (opcional)',
            especialidade: 'string (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/medicos/list',
          description: 'Lista simplificada',
          public: false
        },
        {
          method: 'GET',
          path: '/api/medicos/:id',
          description: 'Detalhes do médico',
          public: false
        }
      ]
    },
    {
      id: 'laudos',
      name: 'Laudos',
      icon: '📄',
      endpoints: [
        {
          method: 'GET',
          path: '/api/laudos',
          description: 'Listar laudos',
          public: false,
          queryParams: {
            page: 'number (default: 1)',
            limit: 'number (default: 20)',
            search: 'string (opcional)',
            dataInicio: 'YYYY-MM-DD (opcional)',
            dataFim: 'YYYY-MM-DD (opcional)'
          }
        },
        {
          method: 'GET',
          path: '/api/laudos/list',
          description: 'Lista simplificada',
          public: false
        },
        {
          method: 'GET',
          path: '/api/laudos/:id',
          description: 'Detalhes do laudo',
          public: false
        }
      ]
    },
    {
      id: 'infraestrutura',
      name: 'Infraestrutura',
      icon: '🏗️',
      endpoints: [
        {
          method: 'GET',
          path: '/api/aparelhos',
          description: 'Listar aparelhos',
          public: false
        },
        {
          method: 'GET',
          path: '/api/laboratorios',
          description: 'Listar laboratórios',
          public: false
        },
        {
          method: 'GET',
          path: '/api/locais',
          description: 'Listar locais',
          public: false
        },
        {
          method: 'GET',
          path: '/api/materiais',
          description: 'Listar materiais',
          public: false
        },
        {
          method: 'GET',
          path: '/api/metodos',
          description: 'Listar métodos',
          public: false
        },
        {
          method: 'GET',
          path: '/api/setores',
          description: 'Listar setores',
          public: false
        }
      ]
    },
    {
      id: 'historico',
      name: 'Histórico',
      icon: '📜',
      endpoints: [
        {
          method: 'GET',
          path: '/api/historico/resultado/:pacienteId/:componenteId',
          description: 'Histórico de resultados',
          public: false
        }
      ]
    },
    {
      id: 'anexos',
      name: 'Anexos',
      icon: '📎',
      endpoints: [
        {
          method: 'GET',
          path: '/api/anexos/:anexoId',
          description: 'Baixar anexo',
          public: false,
          responseType: 'binary'
        }
      ]
    },
    {
      id: 'database',
      name: 'Explorador BD',
      icon: '🗄️',
      endpoints: [
        {
          method: 'GET',
          path: '/api/tables',
          description: 'Listar tabelas',
          public: false
        },
        {
          method: 'GET',
          path: '/api/table/:tableName',
          description: 'Dados da tabela',
          public: false,
          queryParams: {
            limit: 'number (default: 100)'
          }
        },
        {
          method: 'GET',
          path: '/api/table/:tableName/columns',
          description: 'Estrutura da tabela',
          public: false
        }
      ]
    },
    {
      id: 'sistema',
      name: 'Sistema',
      icon: '⚙️',
      endpoints: [
        {
          method: 'GET',
          path: '/api/version',
          description: 'Versão do sistema',
          public: true,
          response: {
            version: 'string',
            name: 'string',
            buildDate: 'string',
            commitHash: 'string',
            commitShort: 'string',
            branch: 'string',
            environment: 'string'
          }
        },
        {
          method: 'GET',
          path: '/api/test',
          description: 'Health check',
          public: true,
          response: {
            message: 'API funcionando!',
            timestamp: '2025-01-20T10:30:00.000Z'
          }
        }
      ]
    }
  ];

  const getMethodClass = (method) => {
    const classes = {
      GET: 'method-get',
      POST: 'method-post',
      PUT: 'method-put',
      DELETE: 'method-delete',
      PATCH: 'method-patch'
    };
    return classes[method] || 'method-default';
  };

  const currentCategory = categories.find(c => c.id === selectedCategory);

  return (
    <div className="api-docs-container">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>📚</span>
          <div>
            <h1 style={styles.pageTitle}>Documentação da API</h1>
            <p style={styles.pageSubtitle}>Sistema de Gestão Laboratorial - Santa Rita</p>
          </div>
        </div>
      </div>
      <div className="api-docs-info">
        <div className="info-item">
          <strong>Base URL (Dev):</strong> http://localhost:3001/api
        </div>
        <div className="info-item">
          <strong>Base URL (Prod):</strong> https://apisantarita.viptecnologia.com.br/api
        </div>
        <div className="info-item">
          <strong>Autenticação:</strong> Bearer Token (JWT)
        </div>
      </div>

      <div className="api-docs-content">
        <div className="api-docs-sidebar">
          <h3>Categorias</h3>
          {categories.map(category => (
            <div
              key={category.id}
              className={`category-item ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedCategory(category.id);
                setSelectedEndpoint(null);
              }}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{category.name}</span>
              <span className="category-count">{category.endpoints.length}</span>
            </div>
          ))}
        </div>

        <div className="api-docs-main">
          {currentCategory && (
            <>
              <div className="category-header">
                <h2>
                  <span className="category-icon-large">{currentCategory.icon}</span>
                  {currentCategory.name}
                </h2>
                <p className="category-description">
                  {currentCategory.endpoints.length} endpoint{currentCategory.endpoints.length > 1 ? 's' : ''} disponível{currentCategory.endpoints.length > 1 ? 'is' : ''}
                </p>
              </div>

              <div className="endpoints-list">
                {currentCategory.endpoints.map((endpoint, index) => (
                  <div
                    key={index}
                    className={`endpoint-card ${selectedEndpoint === index ? 'expanded' : ''}`}
                    onClick={() => setSelectedEndpoint(selectedEndpoint === index ? null : index)}
                  >
                    <div className="endpoint-header">
                      <span className={`method-badge ${getMethodClass(endpoint.method)}`}>
                        {endpoint.method}
                      </span>
                      <code className="endpoint-path">{endpoint.path}</code>
                      {endpoint.public && (
                        <span className="badge-public">Público</span>
                      )}
                      {endpoint.admin && (
                        <span className="badge-admin">Admin</span>
                      )}
                    </div>
                    <p className="endpoint-description">{endpoint.description}</p>

                    {selectedEndpoint === index && (
                      <div className="endpoint-details">
                        {!endpoint.public && (
                          <div className="detail-section">
                            <h4>Autenticação</h4>
                            <code className="code-block">
                              Authorization: Bearer &lt;token&gt;
                            </code>
                          </div>
                        )}

                        {endpoint.queryParams && (
                          <div className="detail-section">
                            <h4>Query Parameters</h4>
                            <table className="params-table">
                              <thead>
                                <tr>
                                  <th>Parâmetro</th>
                                  <th>Tipo/Descrição</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(endpoint.queryParams).map(([key, value]) => (
                                  <tr key={key}>
                                    <td><code>{key}</code></td>
                                    <td>{value}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {endpoint.body && (
                          <div className="detail-section">
                            <h4>Request Body</h4>
                            <pre className="code-block">
                              {JSON.stringify(endpoint.body, null, 2)}
                            </pre>
                          </div>
                        )}

                        {endpoint.response && (
                          <div className="detail-section">
                            <h4>Response (200)</h4>
                            <pre className="code-block">
                              {JSON.stringify(endpoint.response, null, 2)}
                            </pre>
                          </div>
                        )}

                        {endpoint.responseType && (
                          <div className="detail-section">
                            <h4>Response Type</h4>
                            <code className="code-block">
                              Content-Type: {endpoint.responseType}
                            </code>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiDocs;
